/**
 * sqlite3mt frontier publication helpers
 *
 * Mechanical extraction from §96 §8.1.
 *
 * The Control Worker publishes the integrated WAL frontier into SAB4 so that
 * readers can block only when their visible frame is ahead of the integrated
 * frontier in the same WAL cycle.
 */

import { Sab4Scalar, sab4ScalarView } from './sab-layout'

export interface FrontierStateFields {
    currentWalSalt0: number | null
    currentWalSalt1: number | null
    integratedFrameMax: number
}

export interface FrontierSnapshot {
    valid: boolean
    salt0: number
    salt1: number
    integratedFrameMax: number
    waitSeq: number
}

export function frontierStateFieldsEqual(a: FrontierStateFields, b: FrontierStateFields): boolean {
    return a.currentWalSalt0 === b.currentWalSalt0
        && a.currentWalSalt1 === b.currentWalSalt1
        && a.integratedFrameMax === b.integratedFrameMax
}

export function readFrontierSnapshotStable(sab4: SharedArrayBuffer): FrontierSnapshot {
    const scalar = sab4ScalarView(sab4)

    for (;;) {
        const s1 = Atomics.load(scalar, Sab4Scalar.FRONTIER_SEQ)
        if ((s1 & 1) !== 0) {
            continue
        }

        const valid = Atomics.load(scalar, Sab4Scalar.FRONTIER_VALID) !== 0
        const salt0 = Atomics.load(scalar, Sab4Scalar.FRONTIER_SALT0)
        const salt1 = Atomics.load(scalar, Sab4Scalar.FRONTIER_SALT1)
        const integratedFrameMax = Atomics.load(scalar, Sab4Scalar.FRONTIER_INTEGRATED_MAX)
        const waitSeq = Atomics.load(scalar, Sab4Scalar.FRONTIER_WAITSEQ)

        const s2 = Atomics.load(scalar, Sab4Scalar.FRONTIER_SEQ)
        if (s1 === s2 && (s2 & 1) === 0) {
            return {
                valid,
                salt0,
                salt1,
                integratedFrameMax,
                waitSeq,
            }
        }
    }
}

export function publishedFrontierFields(snapshot: FrontierSnapshot): FrontierStateFields {
    return {
        currentWalSalt0: snapshot.valid ? snapshot.salt0 : null,
        currentWalSalt1: snapshot.valid ? snapshot.salt1 : null,
        integratedFrameMax: snapshot.integratedFrameMax,
    }
}

export function notifyFrontierWaiters(sab4: SharedArrayBuffer): number {
    const scalar = sab4ScalarView(sab4)
    const nextWaitSeq = Atomics.add(scalar, Sab4Scalar.FRONTIER_WAITSEQ, 1) + 1
    Atomics.notify(scalar, Sab4Scalar.FRONTIER_WAITSEQ)
    return nextWaitSeq
}

export function publishFrontierStateToSabIfChanged(
    sab4: SharedArrayBuffer,
    next: FrontierStateFields
): boolean {
    const prev = publishedFrontierFields(readFrontierSnapshotStable(sab4))
    if (frontierStateFieldsEqual(prev, next)) {
        return false
    }

    const scalar = sab4ScalarView(sab4)

    Atomics.add(scalar, Sab4Scalar.FRONTIER_SEQ, 1)
    Atomics.store(scalar, Sab4Scalar.FRONTIER_VALID, next.currentWalSalt0 == null ? 0 : 1)
    Atomics.store(scalar, Sab4Scalar.FRONTIER_SALT0, next.currentWalSalt0 ?? 0)
    Atomics.store(scalar, Sab4Scalar.FRONTIER_SALT1, next.currentWalSalt1 ?? 0)
    Atomics.store(scalar, Sab4Scalar.FRONTIER_INTEGRATED_MAX, next.integratedFrameMax)
    Atomics.add(scalar, Sab4Scalar.FRONTIER_SEQ, 1)
    notifyFrontierWaiters(sab4)
    return true
}

export function readerNeedsFrontierWait(
    readerSalt0: number,
    readerSalt1: number,
    readerFrameMax: number,
    snapshot: FrontierSnapshot
): boolean {
    return snapshot.valid
        && readerSalt0 === snapshot.salt0
        && readerSalt1 === snapshot.salt1
        && readerFrameMax > snapshot.integratedFrameMax
}

export function waitUntilReaderFrontierVisible(
    sab4: SharedArrayBuffer,
    readerSalt0: number,
    readerSalt1: number,
    readerFrameMax: number
): 'VISIBLE' | 'FATAL' {
    const scalar = sab4ScalarView(sab4)

    for (;;) {
        if (Atomics.load(scalar, Sab4Scalar.FATAL_FLAG) !== 0) {
            return 'FATAL'
        }

        const snapshot1 = readFrontierSnapshotStable(sab4)
        if (!readerNeedsFrontierWait(readerSalt0, readerSalt1, readerFrameMax, snapshot1)) {
            return 'VISIBLE'
        }

        const waitSeq = snapshot1.waitSeq
        const snapshot2 = readFrontierSnapshotStable(sab4)
        if (!readerNeedsFrontierWait(readerSalt0, readerSalt1, readerFrameMax, snapshot2)) {
            return 'VISIBLE'
        }
        if (snapshot2.waitSeq !== waitSeq) {
            continue
        }

        if (Atomics.load(scalar, Sab4Scalar.FATAL_FLAG) !== 0) {
            return 'FATAL'
        }

        Atomics.wait(scalar, Sab4Scalar.FRONTIER_WAITSEQ, waitSeq)
    }
}
